import { type CSSProperties, useMemo, useRef, useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import type {
  IntakeQuestionnaire,
  IntakeAnswers,
  ValidationError,
  Question,
  Section,
} from './types';
import { validateQuestionnaire } from './validate';
import { parseValidationSchema, validateAnswers } from '@helios/validation';

// ─── Props ───

/** Custom field renderer override — replaces the default QuestionField for a given fieldKey. */
export type FieldRenderer = (props: {
  question: Question;
  value: string | string[] | undefined;
  onChange: (fieldKey: string, val: string | string[]) => void;
  readOnly: boolean;
  hasError: boolean;
}) => React.ReactNode;

export interface IntakeRendererProps {
  questionnaire: IntakeQuestionnaire;
  value: IntakeAnswers;
  onChange: (next: IntakeAnswers) => void;
  mode?: 'fill' | 'preview';
  errors?: ValidationError[];
  /** When true, required empty fields show red border + inline error message */
  showFieldErrors?: boolean;
  /** Raw JSON text from intake_form_definitions.validation_schema */
  validationSchema?: string | null;
  /** Optional per-fieldKey custom renderers that replace the default input widget. */
  fieldRenderers?: Record<string, FieldRenderer>;
}

// ─── Shared Styles ───

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-base, 14px)',
  fontWeight: 'var(--font-weight-medium, 500)',
  lineHeight: 'var(--line-height-base, 20px)',
  letterSpacing: '-0.15px',
  color: 'var(--text, #111827)',
};

const inputBaseStyle: CSSProperties = {
  width: '100%',
  height: 'var(--input-height, 42px)',
  padding: '0 var(--input-padding-x, 16px)',
  borderRadius: 'var(--input-radius, 10px)',
  border: '1px solid var(--border, #e5e7eb)',
  background: 'var(--input-bg, #ffffff)',
  fontSize: 'var(--font-size-base, 14px)',
  lineHeight: 'var(--line-height-base, 20px)',
  letterSpacing: '-0.15px',
  color: 'var(--input-text, #0a0a0a)',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const textareaStyle: CSSProperties = {
  ...inputBaseStyle,
  height: 'auto',
  minHeight: 82,
  padding: '10px var(--input-padding-x, 16px)',
  resize: 'vertical',
};

const readOnlyOverride: CSSProperties = {
  background: 'var(--bg-surface-2, #f9fafb)',
  color: 'var(--text-muted, #6b7280)',
  cursor: 'default',
};

const helpTextStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 12px)',
  lineHeight: 'var(--line-height-sm, 16px)',
  color: 'var(--text-placeholder, #9ca3af)',
};

const asteriskStyle: CSSProperties = {
  color: 'var(--color-danger-text, #dc2626)',
};

const errorBorderOverride: CSSProperties = {
  border: '1px solid var(--color-danger-text, #dc2626)',
};

const fieldErrorStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 12px)',
  lineHeight: 'var(--line-height-sm, 16px)',
  color: 'var(--color-danger-text, #dc2626)',
};

// ─── Error Banner ───

function ErrorBanner({ errors }: { errors: ValidationError[] }) {
  return (
    <div
      style={{
        background: 'var(--color-danger-bg, #fef2f2)',
        border: '1px solid var(--color-danger-border, #fecaca)',
        borderRadius: 'var(--radius-lg, 14px)',
        padding: 'var(--sp-4, 16px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2, 8px)',
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-base, 14px)',
          fontWeight: 'var(--font-weight-semi, 600)',
          color: 'var(--color-danger-text, #dc2626)',
        }}
      >
        Invalid questionnaire ({errors.length} {errors.length === 1 ? 'error' : 'errors'})
      </span>
      <ul
        style={{
          margin: 0,
          paddingLeft: 'var(--sp-5, 20px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-1, 4px)',
        }}
      >
        {errors.map((err) => (
          <li
            key={err.id}
            style={{
              fontSize: 'var(--font-size-sm, 12px)',
              lineHeight: 'var(--line-height-sm, 16px)',
              color: 'var(--color-danger-text, #dc2626)',
            }}
          >
            {err.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Signature Pad ───

function IntakeSignaturePad({
  fieldKey,
  value,
  onChange,
  readOnly,
  hasError,
}: {
  fieldKey: string;
  value: string;
  onChange: (fieldKey: string, val: string) => void;
  readOnly: boolean;
  hasError: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const strokesRef = useRef<Array<{ x: number; y: number }[]>>([]);
  const currentStrokeRef = useRef<{ x: number; y: number }[]>([]);

  // Load existing signature data
  useEffect(() => {
    if (value && canvasRef.current) {
      try {
        const parsed = JSON.parse(value);
        if (parsed.strokes) {
          strokesRef.current = parsed.strokes;
          redraw();
          setHasContent(true);
        }
      } catch {
        // ignore
      }
    }
  }, []); // only on mount

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const stroke of strokesRef.current) {
      if (stroke.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i++) {
        ctx.lineTo(stroke[i].x, stroke[i].y);
      }
      ctx.stroke();
    }
  }, []);

  const getPos = (e: React.PointerEvent): { x: number; y: number } => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (readOnly) return;
    e.preventDefault();
    canvasRef.current?.setPointerCapture(e.pointerId);
    setDrawing(true);
    currentStrokeRef.current = [getPos(e)];
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!drawing || readOnly) return;
    e.preventDefault();
    const pos = getPos(e);
    currentStrokeRef.current.push(pos);
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const pts = currentStrokeRef.current;
    if (pts.length < 2) return;
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(pts[pts.length - 2].x, pts[pts.length - 2].y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!drawing) return;
    e.preventDefault();
    setDrawing(false);
    if (currentStrokeRef.current.length > 1) {
      strokesRef.current.push([...currentStrokeRef.current]);
      setHasContent(true);
      const strokeData = JSON.stringify({ version: 1, strokes: strokesRef.current });
      onChange(fieldKey, strokeData);
    }
    currentStrokeRef.current = [];
  };

  const handleClear = () => {
    strokesRef.current = [];
    currentStrokeRef.current = [];
    setHasContent(false);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx && canvasRef.current) {
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    onChange(fieldKey, '');
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={500}
        height={150}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        style={{
          width: '100%',
          maxWidth: 500,
          height: 150,
          border: hasError
            ? '1px solid var(--color-danger-text, #dc2626)'
            : '1px solid var(--border, #e5e7eb)',
          borderRadius: 'var(--input-radius, 10px)',
          cursor: readOnly ? 'default' : 'crosshair',
          touchAction: 'none',
          background: readOnly ? 'var(--bg-surface-2, #f9fafb)' : '#fff',
          display: 'block',
        }}
      />
      {!readOnly && hasContent && (
        <button
          onClick={handleClear}
          style={{
            marginTop: 6,
            background: 'none',
            border: 'none',
            color: 'var(--color-danger-text, #dc2626)',
            fontSize: 'var(--font-size-xs, 12px)',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          Clear signature
        </button>
      )}
    </div>
  );
}

// ─── Date Picker Styles ───

const datePickerStyles = `
.intake-datepicker-wrapper {
  display: block !important;
  width: 100%;
}
.intake-datepicker-wrapper .intake-datepicker-input {
  width: 100%;
}
.react-datepicker {
  font-family: inherit !important;
  border-radius: var(--radius-lg, 14px) !important;
  border: 1px solid var(--border, #e5e7eb) !important;
  box-shadow: 0 4px 24px rgba(0,0,0,0.12) !important;
  font-size: 14px !important;
}
.react-datepicker__header {
  background: var(--bg-surface-2, #f9fafb) !important;
  border-bottom: 1px solid var(--border, #e5e7eb) !important;
  border-radius: var(--radius-lg, 14px) var(--radius-lg, 14px) 0 0 !important;
  padding-top: 12px !important;
}
.react-datepicker__month-dropdown-container--select select,
.react-datepicker__year-dropdown-container--select select {
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid var(--border, #e5e7eb);
  font-size: 13px;
  font-family: inherit;
}
.react-datepicker__day--selected,
.react-datepicker__day--keyboard-selected {
  background-color: var(--color-primary, #2563eb) !important;
  border-radius: 50% !important;
}
.react-datepicker__day:hover {
  border-radius: 50% !important;
}
.react-datepicker__day {
  width: 2.2rem !important;
  line-height: 2.2rem !important;
  margin: 0.2rem !important;
}
.react-datepicker__day-name {
  width: 2.2rem !important;
  line-height: 2.2rem !important;
  margin: 0.2rem !important;
}
`;

// Inject styles once
let datePickerStylesInjected = false;
function ensureDatePickerStyles() {
  if (datePickerStylesInjected || typeof document === 'undefined') return;
  datePickerStylesInjected = true;
  const style = document.createElement('style');
  style.textContent = datePickerStyles;
  document.head.appendChild(style);
}

// ─── Date Picker ───

const currentYear = new Date().getFullYear();
const datePickerYearRange = [currentYear - 120, currentYear] as const;

function IntakeDatePicker({
  fieldKey,
  value,
  onChange,
  readOnly,
  hasError,
}: {
  fieldKey: string;
  value: string;
  onChange: (fieldKey: string, val: string) => void;
  readOnly: boolean;
  hasError: boolean;
}) {
  ensureDatePickerStyles();

  // Parse YYYY-MM-DD → Date object
  const selected = useMemo(() => {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }, [value]);

  const handleChange = (date: Date | null) => {
    if (!date) {
      onChange(fieldKey, '');
      return;
    }
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    onChange(fieldKey, `${y}-${m}-${d}`);
  };

  return (
    <DatePicker
      selected={selected}
      onChange={handleChange}
      dateFormat="MMMM d, yyyy"
      showMonthDropdown
      showYearDropdown
      dropdownMode="select"
      maxDate={new Date()}
      minDate={new Date(datePickerYearRange[0], 0, 1)}
      placeholderText="Select date"
      readOnly={readOnly}
      disabled={readOnly}
      wrapperClassName="intake-datepicker-wrapper"
      className="intake-datepicker-input"
      popperPlacement="bottom-start"
      customInput={
        <input
          readOnly
          style={{
            ...inputBaseStyle,
            ...(readOnly ? readOnlyOverride : {}),
            ...(hasError ? errorBorderOverride : {}),
            cursor: readOnly ? 'default' : 'pointer',
          }}
        />
      }
    />
  );
}

// ─── Question Field ───

function QuestionField({
  question,
  value,
  onChange,
  readOnly,
  hasError,
}: {
  question: Question;
  value: string | string[] | undefined;
  onChange: (fieldKey: string, val: string | string[]) => void;
  readOnly: boolean;
  hasError: boolean;
}) {
  const stringVal = typeof value === 'string' ? value : '';
  const arrayVal = Array.isArray(value) ? value : [];

  const roStyle = readOnly ? readOnlyOverride : {};
  const errStyle = hasError ? errorBorderOverride : {};

  switch (question.type) {
    case 'text':
      return (
        <input
          type="text"
          value={stringVal}
          onChange={(e) => onChange(question.fieldKey, e.target.value)}
          placeholder={question.helpText}
          readOnly={readOnly}
          style={{ ...inputBaseStyle, ...roStyle, ...errStyle }}
        />
      );

    case 'number':
      return (
        <input
          type="number"
          value={stringVal}
          onChange={(e) => onChange(question.fieldKey, e.target.value)}
          placeholder={question.helpText}
          readOnly={readOnly}
          style={{ ...inputBaseStyle, ...roStyle, ...errStyle }}
        />
      );

    case 'date':
      return (
        <IntakeDatePicker
          fieldKey={question.fieldKey}
          value={stringVal}
          onChange={onChange}
          readOnly={readOnly}
          hasError={hasError}
        />
      );

    case 'textarea':
      return (
        <textarea
          rows={3}
          value={stringVal}
          onChange={(e) => onChange(question.fieldKey, e.target.value)}
          placeholder={question.helpText}
          readOnly={readOnly}
          style={{ ...textareaStyle, ...roStyle, ...errStyle }}
        />
      );

    case 'select':
      return (
        <select
          value={stringVal}
          onChange={(e) => onChange(question.fieldKey, e.target.value)}
          disabled={readOnly}
          style={{ ...inputBaseStyle, ...roStyle, ...errStyle }}
        >
          <option value="" disabled>
            Select...
          </option>
          {question.options?.map((opt) => (
            <option key={opt.id} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );

    case 'multi-select':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2, 8px)' }}>
          {question.options?.map((opt) => {
            const checked = arrayVal.includes(opt.value);
            return (
              <label
                key={opt.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-2, 8px)',
                  cursor: readOnly ? 'default' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={readOnly}
                  onChange={() => {
                    const next = checked
                      ? arrayVal.filter((v) => v !== opt.value)
                      : [...arrayVal, opt.value];
                    onChange(question.fieldKey, next);
                  }}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      );

    case 'yes-no':
      return (
        <div style={{ display: 'flex', gap: 'var(--sp-4, 16px)' }}>
          {['Yes', 'No'].map((opt) => (
            <label
              key={opt}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-2, 8px)',
                fontSize: 'var(--font-size-base, 14px)',
                fontWeight: 'var(--font-weight-medium, 500)',
                lineHeight: 'var(--line-height-base, 20px)',
                color: 'var(--text, #111827)',
                cursor: readOnly ? 'default' : 'pointer',
              }}
            >
              <input
                type="radio"
                name={question.fieldKey}
                value={opt.toLowerCase()}
                checked={stringVal === opt.toLowerCase()}
                onChange={(e) => onChange(question.fieldKey, e.target.value)}
                disabled={readOnly}
                style={{ width: 16, height: 16, accentColor: 'var(--color-primary)' }}
              />
              {opt}
            </label>
          ))}
        </div>
      );

    case 'signature':
      return (
        <IntakeSignaturePad
          fieldKey={question.fieldKey}
          value={stringVal}
          onChange={onChange}
          readOnly={readOnly}
          hasError={hasError}
        />
      );

    default:
      return null;
  }
}

// ─── Question Wrapper ───

function isFieldEmpty(value: string | string[] | undefined): boolean {
  if (value === undefined || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

function QuestionWrapper({
  question,
  value,
  onChange,
  readOnly,
  showFieldErrors,
  advancedErrors,
  customRenderer,
}: {
  question: Question;
  value: string | string[] | undefined;
  onChange: (fieldKey: string, val: string | string[]) => void;
  readOnly: boolean;
  showFieldErrors: boolean;
  advancedErrors: Record<string, string>;
  customRenderer?: FieldRenderer;
}) {
  const requiredError = showFieldErrors && question.required && isFieldEmpty(value);
  const advancedError = advancedErrors[question.fieldKey];
  const hasError = requiredError || !!advancedError;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2, 8px)',
        gridColumn:
          question.layout === 'full' ||
          question.type === 'textarea' ||
          question.type === 'signature'
            ? '1 / -1'
            : undefined,
      }}
    >
      <label style={labelStyle}>
        {question.label}
        {question.required && <span style={asteriskStyle}> *</span>}
      </label>
      {customRenderer ? (
        customRenderer({ question, value, onChange, readOnly, hasError })
      ) : (
        <QuestionField
          question={question}
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          hasError={hasError}
        />
      )}
      {requiredError && <span style={fieldErrorStyle}>This field is required</span>}
      {!requiredError && advancedError && <span style={fieldErrorStyle}>{advancedError}</span>}
      {!hasError &&
        question.helpText &&
        question.type !== 'textarea' &&
        question.type !== 'text' &&
        question.type !== 'number' && <span style={helpTextStyle}>{question.helpText}</span>}
    </div>
  );
}

// ─── Section Card ───

function SectionCard({
  section,
  value,
  onChange,
  readOnly,
  showFieldErrors,
  advancedErrors,
  fieldRenderers,
}: {
  section: Section;
  value: IntakeAnswers;
  onChange: (fieldKey: string, val: string | string[]) => void;
  readOnly: boolean;
  showFieldErrors: boolean;
  advancedErrors: Record<string, string>;
  fieldRenderers?: Record<string, FieldRenderer>;
}) {
  return (
    <div
      style={{
        background: 'var(--bg-surface-2, #f9fafb)',
        border: '1px solid var(--border, #e5e7eb)',
        borderRadius: 'var(--radius-lg, 14px)',
        padding: 'var(--sp-5, 20px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-1, 4px)',
      }}
    >
      <h3
        style={{
          margin: 0,
          fontSize: 'var(--font-size-lg, 16px)',
          fontWeight: 'var(--font-weight-semi, 600)',
          lineHeight: 'var(--line-height-lg, 24px)',
          letterSpacing: '-0.31px',
          color: 'var(--text, #111827)',
        }}
      >
        {section.title}
      </h3>
      {section.description && (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--font-size-sm, 12px)',
            lineHeight: 'var(--line-height-sm, 16px)',
            color: 'var(--text-muted, #6b7280)',
          }}
        >
          {section.description}
        </p>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--sp-4, 16px)',
          marginTop: 'var(--sp-3, 12px)',
        }}
      >
        {section.questions.map((q) => (
          <QuestionWrapper
            key={q.id}
            question={q}
            value={value[q.fieldKey]}
            onChange={onChange}
            readOnly={readOnly}
            showFieldErrors={showFieldErrors}
            advancedErrors={advancedErrors}
            customRenderer={fieldRenderers?.[q.fieldKey]}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───

const EMPTY_FIELD_ERRORS: Record<string, string> = {};

export function IntakeRenderer({
  questionnaire,
  value,
  onChange,
  mode = 'fill',
  errors: externalErrors,
  showFieldErrors = false,
  validationSchema,
  fieldRenderers,
}: IntakeRendererProps) {
  const validationErrors = useMemo(() => {
    if (externalErrors) return externalErrors;
    return validateQuestionnaire(questionnaire);
  }, [questionnaire, externalErrors]);

  const advancedErrors = useMemo(() => {
    if (!validationSchema) return EMPTY_FIELD_ERRORS;
    const parsed = parseValidationSchema(validationSchema);
    if (!parsed) return EMPTY_FIELD_ERRORS;
    const errs = validateAnswers(questionnaire, parsed, value);
    if (errs.length === 0) return EMPTY_FIELD_ERRORS;
    const map: Record<string, string> = {};
    for (const e of errs) map[e.fieldKey] = e.message;
    return map;
  }, [validationSchema, questionnaire, value]);

  const readOnly = mode === 'preview';

  const handleChange = (fieldKey: string, val: string | string[]) => {
    onChange({ ...value, [fieldKey]: val });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-6, 24px)',
      }}
    >
      {validationErrors.length > 0 && <ErrorBanner errors={validationErrors} />}

      {questionnaire.sections.map((section) => (
        <SectionCard
          key={section.id}
          section={section}
          value={value}
          onChange={handleChange}
          readOnly={readOnly}
          showFieldErrors={showFieldErrors}
          advancedErrors={advancedErrors}
          fieldRenderers={fieldRenderers}
        />
      ))}
    </div>
  );
}
